import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  addStakeholders,
  createPrincipleDecision,
  deletePrincipleDecision,
  getPrincipleDecisions,
  getStakeholders,
  removeStakeholder,
  getPrincipleAttachments,
  uploadPrincipleAttachments,
  deletePrincipleAttachment,
  updatePrincipleDecision,
} from '../api/principleDecisionApi'
import type { PersonInput, PrincipleDecisionQuery, PrincipleDecisionUpsert } from '../types'

const KEY = 'principle-decisions'

/** Prensip kararları listesi (arama + sayfalama). `keepPreviousData` → sayfa değişiminde titreme olmaz. */
export function usePrincipleDecisions(query: PrincipleDecisionQuery) {
  return useQuery({
    queryKey: [KEY, query.q ?? '', query.page ?? 0, query.size ?? 20, query.sort ?? ''],
    queryFn: () => getPrincipleDecisions(query),
    placeholderData: keepPreviousData,
  })
}

/** Oluştur/güncelle/sil mutation'ları — her başarıda liste invalidate edilir. */
export function usePrincipleDecisionMutations() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY] })

  const create = useMutation({
    mutationFn: (body: PrincipleDecisionUpsert) => createPrincipleDecision(body),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: PrincipleDecisionUpsert }) =>
      updatePrincipleDecision(id, body),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: number) => deletePrincipleDecision(id),
    onSuccess: invalidate,
  })
  return { create, update, remove }
}

/** Bir prensip kararının paydaşları + ekle/çıkar (5.5–5.7). Yanıt (güncel liste) cache'e yazılır. */
export function useStakeholders(principleId: number | null) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: [KEY, 'stakeholders', principleId],
    queryFn: () => getStakeholders(principleId as number),
    enabled: principleId != null,
  })
  const add = useMutation({
    mutationFn: ({ id, people }: { id: number; people: PersonInput[] }) =>
      addStakeholders(id, people),
    onSuccess: (data, vars) => qc.setQueryData([KEY, 'stakeholders', vars.id], data),
  })
  const remove = useMutation({
    mutationFn: ({ id, employeeId }: { id: number; employeeId: string }) =>
      removeStakeholder(id, employeeId),
    onSuccess: (data, vars) => qc.setQueryData([KEY, 'stakeholders', vars.id], data),
  })
  return { list, add, remove }
}

/**
 * Bir prensip kararının dosya ekleri + yükle/kaldır (108). Liste sorgusu yalnız pop-up açıkken
 * koşar (`enabled`); her mutasyon sonrası yeniden çekilir — dönüş gövdesi yükleyen adını ve
 * yüklenme zamanını taşımadığı için `setQueryData` ile yazmak eksik satır üretirdi.
 */
export function usePrincipleAttachments(principleId: number | null) {
  const qc = useQueryClient()
  const key = [KEY, 'attachments', principleId]
  const list = useQuery({
    queryKey: key,
    queryFn: () => getPrincipleAttachments(principleId as number),
    enabled: principleId != null,
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY, 'attachments', principleId] })
  const upload = useMutation({
    mutationFn: (files: File[]) => uploadPrincipleAttachments(principleId as number, files),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (attachmentId: number) =>
      deletePrincipleAttachment(principleId as number, attachmentId),
    onSuccess: invalidate,
  })
  return { list, upload, remove }
}
